import React, { useEffect, useMemo, useRef, useState } from "react";
import { Play, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { mediaUrl, publicApi } from "../lib/api";
import { parseCaptions, cueAt } from "../lib/captions";

// Anyone who has asked not to be moved around, or who is paying for data, gets
// the poster and a play button rather than something starting on its own.
const quietMode = () =>
  (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) ||
  !!(navigator.connection && navigator.connection.saveData);

/**
 * The promo video: muted autoplay with captions on screen, a tap for sound and
 * a replay. The clip sits inside a link, so the sound and replay controls stop
 * the click rather than navigating with it.
 */
export default function PromoVideo({ promo, preview = false, onRatio }) {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(true);
  const [time, setTime] = useState(0);
  const [failed, setFailed] = useState(false);
  // 16:9 until the browser tells us otherwise, then the clip's own shape — a
  // square or portrait video is shown whole rather than cropped to a letterbox.
  const [ratio, setRatio] = useState(16 / 9);
  // How far this viewer got. Reported once, when the popup closes or the tab
  // goes away — so the admin can see where people stop watching.
  const watch = useRef({ percent: 0, seconds: 0, duration: 0, sound: false, played: false, sent: false });

  const report = (closed) => {
    const w = watch.current;
    if (!w.played || w.sent || preview) return;
    w.sent = true;
    publicApi.trackPromo("video", {
      percent: Math.round(w.percent), seconds: w.seconds,
      duration: w.duration, sound: w.sound, closed,
    });
  };

  const cues = useMemo(() => parseCaptions(promo?.video_captions), [promo?.video_captions]);
  const caption = cueAt(cues, time);
  const autoplayAllowed = promo?.video_autoplay !== false && !quietMode() && !preview;
  const poster = promo?.video_poster ? mediaUrl(promo.video_poster) : (promo?.video ? mediaUrl(`${promo.video}/poster`) : "");

  useEffect(() => {
    const el = ref.current;
    if (!el || !autoplayAllowed) return;
    el.muted = true;
    const go = () => el.play().then(() => setStarted(true)).catch(() => {});
    go();
    // Some browsers refuse a play() issued before the clip can actually play.
    el.addEventListener("canplay", go, { once: true });
    return () => el.removeEventListener("canplay", go);
  }, [autoplayAllowed, promo?.video]);

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const playFromStart = (e, withSound) => {
    stop(e);
    const el = ref.current;
    if (!el) return;
    el.currentTime = 0;
    el.muted = !withSound;
    if (withSound) watch.current.sound = true;
    setMuted(!withSound);
    setStarted(true);
    el.play().catch(() => {});
  };

  useEffect(() => {
    const onHide = () => report(true);
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      report(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readRatio = (e) => {
    const el = e.target;
    if (!el.videoWidth || !el.videoHeight) return;
    const r = el.videoWidth / el.videoHeight;
    setRatio(r);
    onRatio?.(r);
  };

  if (!promo?.video) {
    return (
      <div className="numbers-pattern aspect-video flex items-center justify-center text-white/70 text-sm font-semibold" data-testid="promo-video-empty">
        Upload a video to see it here
      </div>
    );
  }

  return (
    <div className="relative bg-navy-deep" data-testid="promo-video">
      <video
        ref={ref}
        src={mediaUrl(promo.video)}
        poster={poster || undefined}
        muted={muted}
        playsInline
        preload="metadata"
        onLoadedMetadata={readRatio}
        onTimeUpdate={(e) => {
          const el = e.target;
          setTime(el.currentTime);
          const w = watch.current;
          w.played = true;
          w.duration = el.duration || w.duration;
          w.seconds = Math.max(w.seconds, Math.round(el.currentTime * 10) / 10);
          if (el.duration) w.percent = Math.max(w.percent, Math.min(100, (el.currentTime / el.duration) * 100));
        }}
        onEnded={() => { watch.current.percent = 100; report(false); }}
        onPlay={() => setStarted(true)}
        onError={() => { setFailed(true); setStarted(false); }}
        style={{ aspectRatio: String(ratio) }}
        className="w-full max-h-[50vh] object-contain block mx-auto"
        data-testid="promo-video-el"
      />

      {failed && poster && (
        <img src={poster} alt={promo.heading || "Promotion"} className="absolute inset-0 w-full h-full object-contain" />
      )}

      {!started && (
        <span
          role="button"
          tabIndex={preview ? -1 : 0}
          onClick={(e) => playFromStart(e, true)}
          data-testid="promo-video-play"
          aria-label="Play the video"
          className="absolute inset-0 flex items-center justify-center bg-navy-deep/40 hover:bg-navy-deep/20 transition-colors cursor-pointer"
        >
          <span className="w-14 h-14 rounded-full bg-orange text-white flex items-center justify-center shadow-xl transition-transform hover:scale-105">
            <Play size={24} className="ml-0.5" />
          </span>
        </span>
      )}

      {caption && (
        <p
          data-testid="promo-video-caption"
          className="absolute inset-x-0 bottom-12 mx-auto w-fit max-w-[85%] text-center text-xs md:text-sm font-semibold text-white bg-navy-deep/75 backdrop-blur-sm rounded-lg px-3 py-1.5 leading-snug pointer-events-none"
        >
          {caption}
        </p>
      )}

      {started && !preview && (
        <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1.5">
          <button
            type="button"
            onClick={(e) => (muted ? playFromStart(e, true) : (stop(e), (ref.current.muted = true), setMuted(true)))}
            data-testid="promo-video-sound"
            aria-label={muted ? "Turn the sound on" : "Mute the video"}
            className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1.5 rounded-full transition-colors ${
              muted ? "bg-orange hover:bg-orange-dark text-white" : "bg-navy-deep/70 hover:bg-navy-deep text-white"
            }`}
          >
            {muted ? <><Volume2 size={12} /> Sound</> : <VolumeX size={12} />}
          </button>
          <button
            type="button"
            onClick={(e) => playFromStart(e, !muted)}
            data-testid="promo-video-replay"
            aria-label="Replay the video"
            className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-navy-deep/70 hover:bg-navy-deep text-white transition-colors"
          >
            <RotateCcw size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
